import { CalloutPriority } from '../../../../__generated__/globalTypes';
import { CreditCardIcon } from '../../../../icons';
import { toCurrency } from '../../../../utils/formatting';
import BankAccountNameAndAvatar from '../../../BankAccountNameAndAvatar';
import Card from '../../../base/Card';
import { CreditLimitProgressBar } from '../../../CreditLimit';

import type { Callout } from '../../CalloutCard';

export interface IHighCreditUtilizationMetadata {
  balance: number;
  creditLimit: number;
  repairReason?: 'utilization_decreased';
}

interface IHighCreditUtilizationCalloutCardProps {
  callout: Callout;
  /**
   * Called when the user dismisses the callout.
   * @param appropriate - true if helpful, false if not helpful, undefined if not asked
   */
  onDismiss?: (appropriate?: boolean) => void;
  className?: string;
  onClick?: () => void;
  /**
   * 'full' shows full details (account, utilization %), used in carousel.
   * 'compact' shows description only, used in slideout.
   * @default 'full'
   */
  variant?: 'full' | 'compact';
}

export function isHighCreditUtilizationMetadata(metadata: unknown): metadata is IHighCreditUtilizationMetadata {
  if (typeof metadata !== 'object' || metadata === null || !('balance' in metadata) || !('creditLimit' in metadata)) {
    return false;
  }

  const { balance, creditLimit, repairReason } = metadata as IHighCreditUtilizationMetadata;

  if (typeof balance !== 'number' || typeof creditLimit !== 'number') {
    return false;
  }

  if (repairReason !== undefined && repairReason !== 'utilization_decreased') {
    return false;
  }

  return true;
}

const HighCreditUtilizationCalloutCard = ({
  callout,
  onDismiss,
  className,
  onClick,
  variant = 'full',
}: IHighCreditUtilizationCalloutCardProps) => {
  // Validate metadata and ensure we have financial account info
  if (!isHighCreditUtilizationMetadata(callout.metadata) || !callout.financialAccount) {
    return null;
  }

  const metadata = callout.metadata;
  const financialAccount = callout.financialAccount;
  const isP0 = callout.priority === CalloutPriority.P0;
  const utilizationPercentage =
    metadata.creditLimit > 0 ? Math.round((metadata.balance / metadata.creditLimit) * 100) : 0;

  // Normal state - show the callout card
  return (
    <Card severity={isP0 ? 'error' : 'warning'} onClick={onClick} className={className}>
      <Card.Header
        icon={<CreditCardIcon />}
        label="Credit limit"
        onDismiss={onDismiss ? () => onDismiss() : undefined}
      />
      {variant === 'full' ? (
        <Card.Body>
          {/* Row 1: Account (avatar + name + details) | Utilization % */}
          <div className="flex items-center justify-between">
            <BankAccountNameAndAvatar financialAccount={financialAccount} displayNameClassName="font-semibold" />
            <span className="text-error-primary ml-2 self-start text-xl font-semibold">{utilizationPercentage}%</span>
          </div>
          {/* Row 2: Credit limit progress bar */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1">
              <span className="text-tertiary text-sm font-medium">Credit limit:</span>
              <span className="text-primary text-sm font-semibold">{toCurrency(metadata.creditLimit / 100)}</span>
            </div>
            <CreditLimitProgressBar
              className="gap-1.5"
              creditLimit={metadata.creditLimit / 100}
              balance={metadata.balance / 100}
              textClassName="text-sm"
            />
          </div>
        </Card.Body>
      ) : (
        <Card.Body className="py-3">
          <CreditLimitProgressBar
            creditLimit={metadata.creditLimit / 100}
            balance={metadata.balance / 100}
            textClassName="text-xs"
          />
        </Card.Body>
      )}
    </Card>
  );
};

export default HighCreditUtilizationCalloutCard;
